import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import PostApi from 'api/post';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import {
  useInvalidatePostDetailQueries,
  useInvalidatePostsQueries,
} from 'app/modules/posts/hooks/useInvalidatePostsQueries';
import { queryKey } from 'constants/queryKey';
import {
  applyTranslatedImagesToCards,
  isTranslatedImageReady,
} from 'app/modules/posts/utils/translatedImageCards';
import type {
  TranslationImageField,
  TranslationFields,
} from 'types/AiHubTranslation';

export const useApplyTranslation = (
  postId: number,
  options?: { onApplied?: () => void | Promise<void> },
) => {
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invalidatePostDetail = useInvalidatePostDetailQueries();
  const invalidatePosts = useInvalidatePostsQueries();

  const onApplySuccess = async () => {
    await queryClient.refetchQueries(queryKey.postDetail(postId));
    queryClient.invalidateQueries(queryKey.postTranslation(postId));
    invalidatePostDetail({ postId });
    invalidatePosts();
    await options?.onApplied?.();
    toast(
      t('translation.applySuccess', {
        defaultValue: 'Translation applied successfully',
      }),
      'success',
    );
  };

  const onApplyError = (e: ApiClientError) => {
    invalidatePostDetail({ postId });
    toast(
      e?.userMessage ||
        t('translation.applyFailed', {
          defaultValue: 'Failed to apply translation',
        }),
      'error',
    );
  };

  const applyPostText = useMutation(
    (translatedText: string) =>
      PostApi.savePartial({
        postId,
        values: { text: translatedText },
      }),
    {
      onSuccess: onApplySuccess,
      onError: onApplyError,
    },
  );

  const applyFirstComment = useMutation(
    async (translatedComment: string) => {
      const post = await PostApi.detail(postId);
      return PostApi.savePartial({
        postId,
        values: {
          attachments: {
            ...post.attachments,
            firstComment: {
              text: translatedComment,
              status: post.attachments?.firstComment?.status ?? null,
            },
          },
        },
      });
    },
    {
      onSuccess: onApplySuccess,
      onError: onApplyError,
    },
  );

  const applyImages = useMutation(
    async (images: TranslationImageField[]) => {
      const post = await PostApi.detail(postId);
      const cards = applyTranslatedImagesToCards(
        post.attachments?.cards ?? [],
        images,
      );

      return PostApi.savePartial({
        postId,
        values: { attachments: { ...post.attachments, cards } },
      });
    },
    {
      onSuccess: onApplySuccess,
      onError: onApplyError,
    },
  );

  const applyAll = useMutation(
    async (fields: TranslationFields) => {
      const post = await PostApi.detail(postId);
      const updates: any = {};

      if (fields.postText?.translated) {
        updates.text = fields.postText.translated;
      }

      const attachments = { ...post.attachments };
      let attachmentsModified = false;

      if (fields.firstComment?.translated) {
        attachments.firstComment = {
          text: fields.firstComment.translated,
          status: attachments.firstComment?.status ?? null,
        };
        attachmentsModified = true;
      }

      if (fields.images?.some(isTranslatedImageReady)) {
        attachments.cards = applyTranslatedImagesToCards(
          attachments.cards ?? [],
          fields.images,
        );
        attachmentsModified = true;
      }

      if (attachmentsModified) {
        updates.attachments = attachments;
      }

      return PostApi.savePartial({ postId, values: updates });
    },
    {
      onSuccess: onApplySuccess,
      onError: onApplyError,
    },
  );

  return { applyPostText, applyFirstComment, applyImages, applyAll };
};
